import { useEffect, useMemo, useRef, useState } from 'react';
import {
  facilityResearch,
  intelFind,
  intelFindSummary,
  intelSendToCrm,
  intelUnwatch,
  intelWatch,
  type FindLens,
  type FindPage,
  type FindQuery,
  type FindRow,
  type FindSummary,
  type ResearchResult,
} from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { AboutButton } from '../../ui/AboutThis';
import { READ_ONLY_SHORT } from '../../ui/ReadOnly';
import { ErrorState } from '../../ui/states';
import { plusK, type IntelState, type TabProps } from './common';
import {
  BED_CHIPS,
  LENSES,
  SIG,
  US_STATES,
  TYPE_CHIPS,
  colsFor,
  gradeOf,
  serverPayers,
  money,
  n0,
  occSeries,
  ruleVerdict,
  siteOf,
  spark,
  stanceVerdict,
} from './find/model';
import './find/find.css';
import { BuildLabel, BuildTagged, Est, useBuildLabels } from '../../ui/buildLabels';
import { isEstimateWord } from '@aithora/shared';

/** Find's own page state (V4 fd* keys). */
type FdState = IntelState & { fdLens?: FindLens; fdSt?: string; fdTy?: string; fdBd?: string; fdQ?: string; fdSel?: string | null };

const TAG = 'font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;background:rgba(34,39,46,.08);border-radius:4px;padding:1px 5px;';
const CAPS = 'font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;';
const PAGE = 40;

const seg = (on: boolean) =>
  'display:inline-flex;align-items:center;height:28px;padding:0 11px;border-radius:7px;font-size:16px;text-decoration:none;white-space:nowrap;' +
  (on ? 'font-weight:700;color:#2F5C97;background:rgba(79,138,214,.13);' : 'font-weight:500;color:#3C454F;');
/** Track, drawn off for a read-only account (it already sends nothing). */
const OFF = 'opacity:.55;cursor:not-allowed;';
const trackStyle = (on: boolean, big = false, ro = false) =>
  (big
    ? 'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;'
    : 'display:inline-flex;align-items:center;height:24px;padding:0 8px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;') +
  (on ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : 'color:#2F5C97;border:1px solid rgba(47,92,151,.34);') +
  (ro ? OFF : '');

/** The search box waits this long after the last key before asking the server. */
const SEARCH_DEBOUNCE_MS = 250;
/** The quick look researches a row once it has stayed selected this long (research is paid). */
const RESEARCH_DWELL_MS = 600;

/**
 * Atlas Intelligence > Market > Find (Console V4: T15133, aiFindVals L30427). Every US
 * senior-care facility in the catalog, ranked through a lens (Ranked, Distress, Sourcing,
 * Coverage), narrowed by state, type, beds and a search, and acted on per row. Rows and
 * tiles are server-side over the whole catalog (GET /intelligence/find, /find/summary);
 * occupancy and stars are CMS-reported, signals are our rule, money is MODELED. The quick
 * look researches the selected row through Perplexity (GET /facilities/:id/research).
 * Sales sees Find without the deal model and without the Atlas verdict.
 */
export function FindTab(p: TabProps) {
  const st = p.st as FdState;
  const set = p.set as (patch: FdState) => void;
  const { client, sales, readOnly } = p;
  const build = useBuildLabels();
  const lens: FindLens = st.fdLens ?? 'ranked';
  const fSt = st.fdSt ?? 'All';
  const fTy = st.fdTy ?? 'All';
  const fBd = st.fdBd ?? 'All';
  const qRaw = String(st.fdQ ?? '');

  // The view's width picks the columns, as the design's ResizeObserver does.
  const ref = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(1200);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const nw = Math.round(el.getBoundingClientRect().width);
      setW((old) => (Math.abs(nw - old) > 8 ? nw : old));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { cols, wide, mid, narrow } = colsFor(w);

  const [q, setQ] = useState(qRaw.trim());
  useEffect(() => {
    const t = window.setTimeout(() => setQ(qRaw.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [qRaw]);

  const query: FindQuery = useMemo(() => {
    const out: FindQuery = { lens };
    if (fSt !== 'All') out.state = fSt;
    const ty = lens === 'coverage' ? undefined : TYPE_CHIPS[fTy];
    if (ty) out.type = ty;
    if (fBd !== 'All') out.bedBand = fBd;
    if (q) out.q = q;
    return out;
  }, [lens, fSt, fTy, fBd, q]);
  const key = JSON.stringify(query);

  const page = useQuery<FindPage>((signal) => intelFind(client, { query: { ...query, limit: PAGE }, signal }), [client, key]);
  const summary = useQuery<FindSummary>((signal) => intelFindSummary(client, { query, signal }), [client, key]);

  // Tracked: the server's watch list, changed here optimistically.
  const [tracked, setTracked] = useState<Record<string, boolean>>({});
  const isTracked = (r: FindRow) => tracked[r.id] ?? r.watched;
  const toggleTrack = (r: FindRow) => {
    if (readOnly) return;
    const on = !isTracked(r);
    setTracked((t) => ({ ...t, [r.id]: on }));
    // Sales tracks in the CRM (the watch list is not theirs); everyone else watches.
    const call = sales
      ? on
        ? intelSendToCrm(client, { body: { facilityIds: [r.id] } })
        : Promise.resolve(null)
      : on
        ? intelWatch(client, { body: { facilityIds: [r.id] } })
        : intelUnwatch(client, { params: { facilityId: r.id } });
    call.catch(() => setTracked((t) => ({ ...t, [r.id]: !on })));
  };

  const rows = page.data?.rows ?? [];
  const selRow = rows.find((r) => r.id === st.fdSel) ?? rows[0] ?? null;
  const live = page.data?.real !== false;

  const model = (r: FindRow) => {
    if (sales || r.unitType === 'agency' || !r.beds || r.occupancy === null) return;
    const sp = serverPayers(r);
    p.go('deal', {
      aiMapSubj: { id: r.id, name: r.name, beds: r.beds, occ: Math.round(r.occupancy * 100), role: r.typeLabel + ' · ' + (r.state ?? ''), payers: sp.payers, payersLabel: sp.label ?? undefined },
      aiSel: 9999,
      scOcc: undefined,
      scMix: undefined,
      scMargin: undefined,
      scMult: undefined,
    });
  };
  const canModel = (r: FindRow) => !sales && r.unitType !== 'agency' && !!r.beds && r.occupancy !== null;

  const s = summary.data;
  const tile = (label: string, val: string, sub: string, tag: string) => ({ label, val, sub, tag });
  const tiles = [
    tile('Shown', s ? n0(s.matching) : '–', 'of ' + (s ? n0(lens === 'coverage' ? s.agencies : s.residential) : '–') + ' in this slice', live ? 'LIVE' : 'MOCK'),
    tile('Distressed now', s ? n0(s.distress) : '–', 'occupancy falling', live ? 'LIVE' : 'MOCK'),
    tile('Worth buying', s ? n0(s.sourcing) : '–', 'quality gap or turnaround', live ? 'LIVE' : 'MOCK'),
    // The sum of the shown rows' upside (the server sums the filter's rows); a row's Value column is its worth today.
    tile('Value on the table', s ? money(s.upsideM) : '–', 'upside if the shown buildings reached 90% occupancy', 'MODELED'),
  ];
  const catalogN = page.data?.footer?.catalogSize ?? null;
  const lensNote = (LENSES.find((l) => l[0] === lens) ?? LENSES[0])[2];

  const typeChips = lens === 'coverage' ? ['All'] : Object.keys(TYPE_CHIPS);
  const chipRow = (label: string, keyName: 'fdSt' | 'fdTy' | 'fdBd', cur: string, opts: string[]) => (
    <div style={sx('display:flex;align-items:center;gap:2px;')}>
      <span style={sx('font-size:14px;color:#54606C;margin-right:3px;')}>{label}</span>
      {opts.map((o) => (
        <a
          key={o}
          href="#"
          aria-pressed={cur === o}
          onClick={(e) => {
            e.preventDefault();
            set({ [keyName]: o });
          }}
          style={sx(seg(cur === o))}
        >
          {o}
        </a>
      ))}
    </div>
  );

  return (
    <div ref={ref} data-testid="find" style={sx('flex:1 1 auto;min-height:0;min-width:0;display:flex;flex-direction:column;gap:10px;')}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:flex-end;gap:14px;flex-wrap:wrap;row-gap:4px;')}>
        <div style={sx('min-width:0;flex:1 1 360px;')}>
          {/* V9: the explainer opens on the "i" (design h16557). */}
          <div style={sx('font-size:22px;font-weight:800;letter-spacing:-.015em;color:#22272E;line-height:1.15;')}>
            Find
            <AboutButton open={!!p.st.v9i?.find} onToggle={() => p.set({ v9i: { ...p.st.v9i, find: !p.st.v9i?.find } })} gap={8} />
          </div>
          {p.st.v9i?.find && <div style={sx('font-size:16px;color:#3C454F;margin-top:3px;text-wrap:pretty;')}>Every US senior-care facility, ranked by what we can do for it. Pick a lens, narrow it, act on a row.</div>}
        </div>
        <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;font-size:14px;color:#54606C;')}>
          <span>
            <b style={sx('color:#22272E;font-variant-numeric:tabular-nums;')}>{catalogN ? plusK(catalogN) : '–'}</b> facilities in the catalog
          </span>
          <span style={sx(TAG)}>{live ? 'LIVE ROWS' : 'MOCK ROWS'}</span>
        </div>
      </div>

      <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:0;border-top:1px solid rgba(34,39,46,.14);border-bottom:1px solid rgba(34,39,46,.14);')}>
        {tiles.map((t) => (
          <div key={t.label} style={sx('padding:10px 14px 10px 0;display:flex;align-items:center;gap:12px;min-width:0;')}>
            <div style={sx('min-width:0;flex:1 1 auto;')}>
              {/* The label stays whole on one line; the tag wraps under it before anything is cut. */}
              <div style={sx('display:flex;align-items:center;flex-wrap:wrap;gap:2px 6px;min-width:0;')}>
                <span style={sx('font-size:14px;font-weight:600;color:#54606C;white-space:nowrap;')}>{t.label}</span>
                {build && <BuildTagged tag={t.tag}><span style={sx(TAG + 'flex:0 0 auto;')}>{t.tag}</span></BuildTagged>}
              </div>
              <div style={sx('font-size:26px;font-weight:800;letter-spacing:-.02em;font-variant-numeric:tabular-nums;color:#22272E;line-height:1.1;margin-top:2px;')}>
                <span>{t.val}</span>
                {!build && isEstimateWord(t.tag) && <Est />}
              </div>
              <div style={sx('font-size:14px;color:#54606C;line-height:1.3;text-wrap:pretty;')}>
                <span>{t.sub}</span>
              </div>
            </div>
            {/* The tiles' 12-month lines need a history of these counts, which the catalog does not keep yet: the space stays, empty. */}
            {!narrow && <svg width="84" height="22" viewBox="0 0 84 22" aria-hidden="true" style={sx('flex:0 0 84px;overflow:visible;')} />}
          </div>
        ))}
      </div>

      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;')}>
        <div role="tablist" aria-label="Lens" style={sx('display:flex;align-items:center;gap:2px;')}>
          {LENSES.map(([k, label, title]) => (
            <a
              key={k}
              href="#"
              role="tab"
              aria-selected={lens === k}
              title={title}
              className="intel-h-list"
              onClick={(e) => {
                e.preventDefault();
                set({ fdLens: k, fdSel: null });
              }}
              style={sx(seg(lens === k))}
            >
              {label}
            </a>
          ))}
        </div>
        <span style={sx('width:1px;height:18px;background:rgba(34,39,46,.14);')} />
        <label style={sx('display:flex;align-items:center;gap:5px;font-size:14px;color:#54606C;')}>
          State
          <select
            aria-label="State"
            value={fSt}
            onChange={(e) => set({ fdSt: e.target.value })}
            style={sx('height:28px;border-radius:7px;border:1px solid rgba(34,39,46,.18);background:#FCFAF4;color:#22272E;font-size:16px;padding:0 6px;')}
          >
            <option value="All">All states</option>
            {Object.entries(US_STATES).map(([code, nm]) => (
              <option key={code} value={code}>{nm}</option>
            ))}
          </select>
        </label>
        {chipRow('Type', 'fdTy', fTy, typeChips)}
        {chipRow('Beds', 'fdBd', fBd, BED_CHIPS)}
        <input
          value={qRaw}
          onChange={(e) => set({ fdQ: e.target.value })}
          placeholder="Search name or city"
          aria-label="Search name or city"
          style={sx('margin-left:auto;height:30px;width:200px;max-width:100%;box-sizing:border-box;padding:0 10px;border-radius:7px;border:1px solid rgba(34,39,46,.2);background:#FCFAF4;font:inherit;font-size:16px;color:#22272E;outline:none;')}
        />
      </div>

      <div style={sx('flex:1 1 auto;min-height:0;display:grid;grid-template-columns:minmax(0,1fr) ' + (w >= 1180 ? '290px' : w >= 1000 ? '240px' : '220px') + ';gap:' + (mid ? '16px' : '12px') + ';')}>
        <div style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;border-top:1px solid rgba(34,39,46,.14);')}>
          <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px;padding:6px 12px 0;font-size:14px;color:#54606C;')}>
            <span>{lensNote}</span>
          </div>
          <div style={sx('flex:1 1 auto;min-height:0;overflow:auto;')}>
            <div style={sx('position:sticky;top:0;z-index:2;background:#F7F3EA;padding:7px 12px;border-bottom:1px solid rgba(34,39,46,.14);font-size:14px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#54606C;')}>
              <div style={sx('display:grid;grid-template-columns:' + cols + ';gap:8px;align-items:center;')}>
                <span>#</span>
                <span>Facility</span>
                <span>ST</span>
                <span style={sx('text-align:right;')}>Occ</span>
                {mid && (
                  <>
                    <span style={sx('text-align:right;')}>Beds</span>
                    <span>12 mo</span>
                    <span>Gr</span>
                  </>
                )}
                <span>Signal</span>
                {wide && (
                  <>
                    <span style={sx('text-align:right;')} title="Modeled worth today, at current occupancy">Value today</span>
                    <span>Vs peers</span>
                  </>
                )}
                {mid && <span />}
              </div>
            </div>
            {page.error ? (
              <ErrorState error={page.error} onRetry={page.reload} />
            ) : (
              rows.map((r, k) => (
                <Row
                  key={r.id}
                  r={r}
                  rank={k + 1}
                  cols={cols}
                  wide={wide}
                  mid={mid}
                  sel={!!selRow && selRow.id === r.id}
                  tracked={isTracked(r)}
                  canModel={canModel(r)}
                  ro={readOnly}
                  onOpen={() => set({ fdSel: r.id })}
                  onTrack={() => {
                    toggleTrack(r);
                    set({ fdSel: r.id });
                  }}
                  onModel={() => model(r)}
                />
              ))
            )}
            {page.data && rows.length === 0 && (
              <div style={sx('padding:22px 12px;display:flex;align-items:center;gap:12px;font-size:16px;color:#3C454F;')}>
                Nothing matches these filters.
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    set({ fdSt: 'All', fdTy: 'All', fdBd: 'All', fdQ: '' });
                  }}
                  style={sx('color:#2F5C97;font-weight:600;text-decoration:none;')}
                >
                  Clear filters
                </a>
              </div>
            )}
          </div>
        </div>
        <div style={sx('min-width:0;min-height:0;overflow:auto;border-top:1px solid rgba(34,39,46,.14);padding-top:10px;display:flex;flex-direction:column;gap:10px;')}>
          {selRow && (
            <QuickLook
              key={selRow.id}
              p={p}
              r={selRow}
              tracked={isTracked(selRow)}
              canModel={canModel(selRow)}
              onTrack={() => toggleTrack(selRow)}
              onModel={() => model(selRow)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ r, rank, cols, wide, mid, sel, tracked, canModel, ro = false, onOpen, onTrack, onModel }: {
  r: FindRow;
  rank: number;
  cols: string;
  wide: boolean;
  mid: boolean;
  sel: boolean;
  tracked: boolean;
  canModel: boolean;
  ro?: boolean;
  onOpen: () => void;
  onTrack: () => void;
  onModel: () => void;
}) {
  const agency = r.unitType === 'agency';
  const series = agency ? null : occSeries(r);
  const g = gradeOf(r.stars);
  const sg = SIG[r.signal] ?? SIG.Steady;
  const up = series ? series[series.length - 1] >= series[0] : true;
  return (
    <div
      role="row"
      aria-selected={sel}
      className="intel-find-row"
      onClick={onOpen}
      style={sx('padding:0 12px;min-height:38px;border-bottom:1px solid rgba(34,39,46,.08);cursor:pointer;' + (sel ? 'background:rgba(79,138,214,.10);' : ''))}
    >
      <div style={sx('display:grid;grid-template-columns:' + cols + ';gap:8px;align-items:center;min-height:38px;font-size:16px;')}>
        <span style={sx('font-variant-numeric:tabular-nums;color:#54606C;font-size:14px;')}>{String(rank)}</span>
        <span style={sx('min-width:0;display:flex;flex-direction:column;')}>
          <span style={sx('font-weight:700;color:#22272E;overflow-wrap:break-word;')}>{r.name}</span>
          <span style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')}>{(r.city ?? '') + ' · ' + r.typeLabel}</span>
        </span>
        <span style={sx('color:#3C454F;')}>{r.state ?? ''}</span>
        <span style={sx('text-align:right;font-variant-numeric:tabular-nums;font-weight:600;')}>{agency || r.occupancy === null ? '–' : Math.round(r.occupancy * 100) + '%'}</span>
        {mid && (
          <>
            <span style={sx('text-align:right;font-variant-numeric:tabular-nums;')}>{agency || r.beds === null ? '–' : String(r.beds)}</span>
            <span>
              {series && (
                <svg width="58" height="18" viewBox="0 0 58 18" style={sx('display:block;overflow:visible;')} aria-label="Occupancy, 12 months">
                  <polyline points={spark(series, 58, 18)} fill="none" stroke={up ? '#1F6B52' : '#B3261E'} strokeWidth="1.5" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span style={sx('font-weight:800;font-size:16px;color:' + g[1] + ';')}>{g[0]}</span>
          </>
        )}
        <span>
          <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:700;white-space:nowrap;color:' + sg[0] + ';background:' + sg[1] + ';')}>{r.signal}</span>
        </span>
        {wide && (
          <>
            <span style={sx('text-align:right;font-variant-numeric:tabular-nums;font-weight:600;')}>{money(r.money?.value)}</span>
            <span
              title={r.peerPct === null ? 'No peer rank: too few scored peers of this type in this state' : r.peerPct + 'th percentile vs peers'}
              style={sx('position:relative;display:block;height:4px;border-radius:2px;background:linear-gradient(90deg,rgba(179,38,30,.22),rgba(84,96,108,.18) 40%,rgba(47,138,111,.25));')}
            >
              {r.peerPct !== null && (
                <span style={sx('position:absolute;top:-2px;left:calc(' + r.peerPct + '% - 4px);width:8px;height:8px;border-radius:50%;background:' + (r.peerPct >= 50 ? '#1F6B52' : r.peerPct >= 25 ? '#4A5866' : '#B3261E') + ';')} />
              )}
            </span>
          </>
        )}
        {mid && (
          <span style={sx('display:flex;align-items:center;gap:5px;justify-content:flex-end;')}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onTrack();
              }}
              aria-disabled={ro || undefined}
              title={ro ? READ_ONLY_SHORT : undefined}
              style={sx(trackStyle(tracked, false, ro))}
            >
              {tracked ? 'Tracked' : 'Track'}
            </a>
            {canModel && (
              <a
                href="#"
                className="intel-ask"
                title="Run this building in Scenario (payer split and margin are modeled)"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onModel();
                }}
                style={sx('display:inline-flex;align-items:center;height:24px;padding:0 8px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;color:#fff;background:#2F5C97;')}
              >
                Model
              </a>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function QuickLook({ p, r, tracked, canModel, onTrack, onModel }: { p: TabProps; r: FindRow; tracked: boolean; canModel: boolean; onTrack: () => void; onModel: () => void }) {
  const agency = r.unitType === 'agency';
  const series = agency ? null : occSeries(r);
  const up = series ? series[series.length - 1] >= series[0] : true;
  const facts: [string, string][] = agency
    ? [
        ['Type', r.typeLabel],
        ['CMS stars', r.stars === null ? '–' : r.stars + '★'],
        ['Quality', r.score?.v === null || r.score?.v === undefined ? '–' : String(Math.round(r.score.v))],
        ['Peer rank', r.peerPct === null ? '–' : r.peerPct + 'th pct'],
      ]
    : [
        ['Occupancy', r.occupancy === null ? '–' : Math.round(r.occupancy * 100) + '%'],
        ['CMS stars', r.stars === null ? '–' : r.stars + '★'],
        ['Value today', money(r.money?.value)],
        ['Upside at 90% occupancy', money(r.money?.upside)],
      ];

  // Research the row once it stays selected (Perplexity finds, cached a day server-side).
  const [ask, setAsk] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setAsk(true), RESEARCH_DWELL_MS);
    return () => window.clearTimeout(t);
  }, []);
  const research = useQuery<ResearchResult>((signal) => facilityResearch(p.client, { params: { id: r.id }, signal }), [p.client, r.id], { enabled: ask });
  const res = research.data;
  const findings = res?.findings ?? [];
  const sources = findings.slice(0, 3).map((x) => ({ n: siteOf(x.sources[0]), t: x.text }));
  const verdict = res?.live && res.verdict?.stance ? stanceVerdict(res.verdict.stance, res.verdict.line) : ruleVerdict(r);

  return (
    <>
      <div>
        <div style={sx(CAPS)}>Quick look</div>
        <div style={sx('font-size:16px;font-weight:800;color:#22272E;margin-top:4px;line-height:1.25;text-wrap:pretty;')}>
          <span>{r.name}</span>
        </div>
        <div style={sx('font-size:16px;color:#54606C;margin-top:2px;')}>
          <span>{(r.city ?? '') + ', ' + (r.state ?? '') + ' · ' + r.typeLabel + (agency || r.beds === null ? '' : ' · ' + r.beds + ' beds')}</span>
        </div>
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 12px;')}>
        {facts.map(([k, v]) => (
          <div key={k} style={sx('min-width:0;')}>
            <div style={sx('font-size:14px;color:#54606C;')}>
              <span>{k}</span>
            </div>
            <div style={sx('font-size:17px;font-weight:800;font-variant-numeric:tabular-nums;color:#22272E;')}>
              <span>{v}</span>
            </div>
          </div>
        ))}
      </div>
      {series && (
        <div>
          <svg width="100%" height="40" viewBox="0 0 250 40" preserveAspectRatio="none" style={sx('display:block;overflow:visible;')} aria-label="Occupancy, 12 months">
            <polyline points={spark(series, 250, 40)} fill="none" stroke={up ? '#1F6B52' : '#B3261E'} strokeWidth="1.8" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          </svg>
          <div style={sx('font-size:14px;color:#54606C;margin-top:3px;')}>
            <span>{'Occupancy, 12 months: ' + Math.round(series[0] * 100) + '% to ' + Math.round(series[series.length - 1] * 100) + '%'}</span>
          </div>
        </div>
      )}
      {/* The Atlas verdict is internal acquisition thinking: never shown to Sales (handoff, research + verdict). */}
      {!p.sales && (
        <div style={sx('border-top:1px solid rgba(34,39,46,.12);padding-top:9px;')}>
          <div style={sx('display:flex;align-items:center;gap:7px;')}>
            <span style={sx(CAPS)}>Atlas verdict</span>
            <BuildLabel><span style={sx(TAG)}>MODELED</span></BuildLabel>
          </div>
          <div style={sx('font-size:18px;font-weight:800;color:' + verdict[1] + ';margin-top:3px;')}>
            <span>{verdict[0]}</span>
          </div>
          <div style={sx('font-size:16px;color:#3C454F;line-height:1.45;margin-top:2px;text-wrap:pretty;')}>
            <span>{verdict[2]}</span>
          </div>
        </div>
      )}
      <div style={sx('border-top:1px solid rgba(34,39,46,.12);padding-top:9px;')}>
        <div style={sx('display:flex;align-items:center;gap:7px;')}>
          <span style={sx(CAPS)}>Research</span>
          <span style={sx('font-size:14px;color:#54606C;')}>via Perplexity</span>
          <span style={sx(TAG)}>{res ? (res.live ? 'LIVE' : 'OFF') : '…'}</span>
        </div>
        {sources.map((x, i) => (
          <div key={i} style={sx('display:flex;flex-direction:column;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
            <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{x.n}</span>
            <span style={sx('font-size:16px;color:#3C454F;')}>{x.t}</span>
          </div>
        ))}
        {res && sources.length === 0 && (
          <div style={sx('font-size:16px;color:#3C454F;padding:6px 0;')}>
            <span>{res.note ?? (res.live ? 'Nothing reported about this facility was found.' : 'Live research is not available.')}</span>
          </div>
        )}
        {!res && research.error && (
          <div style={sx('font-size:16px;color:#3C454F;padding:6px 0;')}>
            <span>Research failed. Nothing was saved.</span>
          </div>
        )}
        {!res && !research.error && (
          <div style={sx('font-size:16px;color:#54606C;padding:6px 0;')}>
            <span>Researching public sources…</span>
          </div>
        )}
      </div>
      <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
        {(!p.readOnly || tracked) && (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onTrack();
            }}
            aria-disabled={p.readOnly || undefined}
            title={p.readOnly ? READ_ONLY_SHORT : undefined}
            style={sx(trackStyle(tracked, true, p.readOnly))}
          >
            {tracked ? 'Tracked' : 'Track'}
          </a>
        )}
        {canModel && (
          <a
            href="#"
            className="intel-ask"
            onClick={(e) => {
              e.preventDefault();
              onModel();
            }}
            style={sx('display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;color:#fff;background:#2F5C97;')}
          >
            Model the deal
          </a>
        )}
      </div>
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          p.go('relmap');
        }}
        style={sx('align-self:flex-start;font-size:16px;font-weight:600;color:#2F5C97;text-decoration:none;')}
      >
        See who it is connected to
      </a>
    </>
  );
}
